import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../../constants/axiosInstance';
import { Star, Send, X,ChevronLeft,ChevronRight } from 'lucide-react';
import { sessionService } from '../../services/sessionService'; // Make sure path is correct


const Alert = ({ children, variant = 'destructive' }) => (
    <div className={`p-4 rounded-md ${
      variant === 'destructive' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
    }`}>
      {children}
    </div>
  );
  
  const ReviewForm = ({ onReviewSubmitted, productId, orderId }) => {
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [hoverRating, setHoverRating] = useState(0);
  
    const ratingMessages = {
      1: "Poor - Not recommended",
      2: "Fair - Could be better",
      3: "Good - Met expectations",
      4: "Great - Very satisfied",
      5: "Excellent - Highly recommended"
    };
  
    useEffect(() => {
      // Validate required props
      if (!productId || !orderId) {
        console.error('ReviewForm: Missing required props:', { productId, orderId });
        setError('Unable to submit review at this time');
      }
    }, [productId, orderId]);
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError('');
  
      if (!sessionService.isSessionValid()) {
        setError('Your session has expired. Please log in again.');
        setIsSubmitting(false);
        return;
      }
  
      try {
        const reviewData = {
          rating,
          comment,
          productId: productId.toString(),
          orderId: orderId.toString()
        };
  
        const response = await axiosInstance.post('/api/v1/reviews/create', reviewData);
  
        if (response.data.success) {
          onReviewSubmitted();
          setRating(0);
          setComment('');
        } else {
          setError(response.data.message || 'Failed to submit review');
        }
      } catch (err) {
        console.error('Review submission error:', err);
        setError(err.response?.data?.message || 'Failed to submit review');
      } finally {
        setIsSubmitting(false);
      }
    };

    if (!productId || !orderId) {
      return (
        <div className="bg-yellow-50 p-4 rounded-md text-yellow-700">
          Unable to load review form. Missing required information.
        </div>
      );
    }
  
    return (
      <form onSubmit={handleSubmit} className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-xl shadow-lg mb-8 max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-800">Share Your Experience</h3>
        </div>
  
        {error && (
          <Alert variant="destructive" className="mb-4">
            {error}
          </Alert>
        )}
  
        <div className="mb-8">
          <label className="block text-sm font-medium mb-2 text-gray-700">How would you rate this product?</label>
          <div className="flex flex-col items-center space-y-4">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  className="transform transition-transform hover:scale-110 focus:outline-none"
                  onClick={() => setRating(value)}
                  onMouseEnter={() => setHoverRating(value)}
                  onMouseLeave={() => setHoverRating(0)}
                >
                  <Star
                    className={`w-8 h-8 ${
                      value <= (hoverRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    } transition-colors duration-200`}
                  />
                </button>
              ))}
            </div>
            {(rating || hoverRating) && (
              <p className="text-sm font-medium text-gray-600">
                {ratingMessages[hoverRating || rating]}
              </p>
            )}
          </div>
        </div>
  
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2 text-gray-700">Tell us more about your experience</label>
          <div className="relative">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white resize-none transition-all duration-200"
              rows="4"
              required
              minLength="10"
              maxLength="500"
              placeholder="What did you like or dislike? How was the quality?"
            />
            <span className="absolute bottom-2 right-2 text-xs text-gray-400">
              {comment.length}/500
            </span>
          </div>
        </div>
  
        <button
          type="submit"
          disabled={isSubmitting || !rating || !comment}
          className="w-full py-3 px-6 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2 font-medium"
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Submitting...</span>
            </div>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>Submit Review</span>
            </>
          )}
        </button>
      </form>
    );
  };
  

  const ReviewsList = ({ reviews }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const reviewsPerPage = 3;
    const totalPages = Math.ceil(reviews.length / reviewsPerPage);
    
    const startIndex = currentPage * reviewsPerPage;
    const visibleReviews = reviews.slice(startIndex, startIndex + reviewsPerPage);
  
    const nextPage = () => {
      setCurrentPage((prev) => (prev + 1) % totalPages);
    };
  
    const prevPage = () => {
      setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
    };
  
    return (
      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4">
          {visibleReviews.map((review) => (
            <div 
              key={review._id} 
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden border border-gray-100 flex flex-col"
            >
              {/* Review Header */}
              <div className="p-6 bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center text-white font-semibold shrink-0">
                    {review.userName?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.userName}</h4>
                    <div className="flex items-center mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          className={`${
                            i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
  
              {/* Review Content */}
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-600 break-words whitespace-pre-line flex-1">
                  {review.comment}
                </p>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <time className="text-sm text-gray-500">
                    {new Date(review.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </time>
                </div>
              </div>
            </div>
          ))}
        </div>
  
        {/* Navigation Controls - Only show if there are more than 3 reviews */}
        {reviews.length > 3 && (
          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              onClick={prevPage}
              className="p-2 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors duration-200"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600" />
            </button>
  
            <div className="flex items-center gap-2">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPage(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    currentPage === index 
                      ? 'bg-purple-600 w-4' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to page ${index + 1}`}
                />
              ))}
            </div>
  
            <button
              onClick={nextPage}
              className="p-2 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors duration-200"
              aria-label="Next page"
            >
              <ChevronRight className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        )}
      </div>
    );
  };


const Reviews = ({ productId, orderId, onReviewSubmitted, isHomePage = false }) => {
    const [reviews, setReviews] = useState([]);
    const [canReview, setCanReview] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);  
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
      // Check authentication status
      const checkAuth = () => {
        const isValid = sessionService.isSessionValid();
        setIsAuthenticated(isValid);
      };
  
      checkAuth();
      // Update session activity timestamp when component mounts
      sessionService.updateLastActivity();
    }, []);
  
    const fetchReviews = async () => {
      try {
        const url = '/api/v1/reviews/getCustomerReviews' + 
          (productId ? `?productId=${productId}` : '');
        const response = await axiosInstance.get(url);
        
        if (response.data.success) {
          setReviews(response.data.data);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          // Handle unauthorized error
          setError('Please log in to view reviews');
          sessionService.handleSessionExpiration();
        } else {
          console.error('Error fetching reviews:', err);
          setError('Failed to load reviews');
        }
      }
    };
  
    const checkReviewEligibility = async () => {
      if (!productId || !orderId || isHomePage || !isAuthenticated) {
        setCanReview(false);
        return;
      }
      
      try {
        const response = await axiosInstance.get(
          `/api/v1/reviews/check-review-eligibility/${orderId}/${productId}`
        );
        
        setCanReview(response.data.canReview);
      } catch (err) {
        console.error('Error checking review eligibility:', err);
        if (err.response?.status === 401) {
          sessionService.handleSessionExpiration();
        }
        setCanReview(false);
      }
    };
  
    useEffect(() => {
      const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
          await fetchReviews();
          if (!isHomePage && isAuthenticated) {
            await checkReviewEligibility();
          }
        } catch (err) {
          console.error('Error loading data:', err);
          setError('Failed to load review data');
        } finally {
          setLoading(false);
        }
      };
  
      loadData();
    }, [productId, isHomePage, orderId, isAuthenticated]);
  
    const handleReviewSubmitted = async () => {
      if (!sessionService.isSessionValid()) {
        setError('Your session has expired. Please log in again.');
        return;
      }
  
      try {
        sessionService.updateLastActivity();
        await fetchReviews();
        await checkReviewEligibility();
        if (onReviewSubmitted) {
          onReviewSubmitted();
        }
      } catch (err) {
        console.error('Error after review submission:', err);
        setError('Failed to refresh reviews');
      }
    };

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {isHomePage ? 'What Our Customers Say' : 'Customer Reviews'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Read authentic reviews from our valued customers about their experiences.
            </p>
          </div>
  
          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
            </div>
          ) : (
            <>
              {canReview && <ReviewForm onReviewSubmitted={handleReviewSubmitted} productId={productId} orderId={orderId} />}
              
              {reviews.length > 0 ? (
                // Reviews Grid
                <ReviewsList reviews={reviews} />
              ) : (
                // Empty State
                <div className="text-center py-12 px-4">
                  <div className="bg-white rounded-lg p-8 max-w-md mx-auto shadow-sm">
                    <div className="text-gray-400 mb-4">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-gray-600">No reviews available yet. Be the first to share your experience!</p>
                  </div>
                </div>
              )}
  
              {/* Review Stats */}
              {reviews.length > 0 && (
                <div className="mt-12 px-4">
                  <div className="bg-white rounded-xl shadow-sm p-6 max-w-2xl mx-auto">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="border-r border-gray-200">
                        <div className="text-2xl font-bold text-purple-600">{reviews.length}</div>
                        <div className="text-sm text-gray-500">Total Reviews</div>
                      </div>
                      <div className="border-r border-gray-200">
                        <div className="text-2xl font-bold text-purple-600">
                          {(reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length).toFixed(1)}
                        </div>
                        <div className="text-sm text-gray-500">Average Rating</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-purple-600">
                          {reviews.filter(review => review.rating === 5).length}
                        </div>
                        <div className="text-sm text-gray-500">5-Star Reviews</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    );
  };
  
  export default Reviews;